// @flow
import { memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as csx from 'csstips';
import classify from '@src/utils/typestyle';
import InfiniteScroll from 'react-infinite-scroller';
import _isEqual from 'lodash/isEqual';

import type { DiamondType } from '@types/diamond';

import DiamondDatagridRow from '@components/datagrids/rows/DiamondDatagridRow';

const classes = classify({
  gridContainer: {
    ...csx.vertical,
    width: '1000px',
    maxWidth: '100%'
  }
});

type TProps = {
  rowResponsiveColumns: Object,
  dyor: boolean
};

function diamondsAreEqual(prev: Array<DiamondType>, next: Array<DiamondType>) {
  if (prev.length !== next.length) {
    return false;
  }

  // If they are both 0 length, then they must be the same
  if (next.length === 0) return true;

  const firstOldDiamond = prev[0];
  const lastOldDiamond = prev[prev.length - 1];
  const firstNewDiamond = next[0];
  const lastNewDiamond = next[next.length - 1];

  const firstOldSku = firstOldDiamond['list_service_diamond_id'];
  const lastOldSku = lastOldDiamond['list_service_diamond_id'];
  const firstNewSku = firstNewDiamond['list_service_diamond_id'];
  const lastNewSku = lastNewDiamond['list_service_diamond_id'];

  if (firstOldSku !== firstNewSku) return false;
  if (lastOldSku !== lastNewSku) return false;

  // as a last resort, allow deep comparison of all objects
  return _isEqual(prev, next);
}

function DiamondDatagrid(props: TProps) {
  const comparisonIds = useSelector(state => state.diamondComparison.ids);
  const matches = useSelector(
    state => state.diamonds.matches,
    diamondsAreEqual
  );
  const isLoading = useSelector(state => state.diamondSearch.loading);
  const matchCount = useSelector(state => state.diamonds.count);
  const allDiamondsAreLoaded = matches.length === matchCount;
  const isDiamondSelected = (id: number) => comparisonIds.indexOf(id) > -1;
  const currentPage = useSelector(state => state.diamondSearch.page);
  const dispatch = useDispatch();

  const handleInfiniteLoad = useCallback(() => {
    if (isLoading) return;

    dispatch({
      type: 'UPDATE_DIAMOND_SEARCH_PAGE',
      item: currentPage + 1
    });
    dispatch({ type: 'FETCH_DIAMONDS', looseDiamonds: !props.dyor });
  }, [isLoading, dispatch, currentPage, props.dyor]);

  const handleRowSelection = useCallback(
    diamondId => {
      dispatch({ type: 'TOGGLE_DIAMOND_FOR_COMPARISON', item: diamondId });
    },
    [dispatch]
  );

  const tableMinimumWidth = props.rowResponsiveColumns['tableMinimumWidth'];

  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (typeof window === 'undefined') return;
    if (allDiamondsAreLoaded) return;
    if (isLoading) return;

    window.requestAnimationFrame(() => {
      const scrollHeight =
        document.documentElement.scrollHeight || document.body.scrollHeight;
      const viewportHeight = document.documentElement.clientHeight;

      if (viewportHeight >= scrollHeight) handleInfiniteLoad();
    });
  }, [isLoading, allDiamondsAreLoaded, handleInfiniteLoad]);

  return (
    <div
      className={classes.gridContainer}
      style={{ minWidth: tableMinimumWidth }}
    >
      <InfiniteScroll
        initialLoad={false}
        threshold={200}
        loadMore={handleInfiniteLoad}
        hasMore={!allDiamondsAreLoaded}
        useWindow
      >
        {matches.map((d, i) => {
          return (
            <DiamondDatagridRow
              dyor={props.dyor}
              diamond={d}
              key={d.list_service_diamond_id}
              onSelection={handleRowSelection}
              selected={isDiamondSelected(d.list_service_diamond_id)}
              responsiveColumnClasses={props.rowResponsiveColumns.classes}
            />
          );
        })}
      </InfiniteScroll>
    </div>
  );
}

export default memo(DiamondDatagrid, _isEqual);
